// Landing pages for search: three service pages and one page on how working
// together works. Shared with scripts/prerender-heads.js, which writes the
// static HTML from this same list, so the copy a crawler reads is the copy on
// the page. New pages also need an entry in public/sitemap.xml.
//
// Every claim here should hold up on the intro call: no prices, no promised
// timelines, and only work that is on the site.

const pages = [
    {
        id: 'ai-consulting',
        path: '/services/ai-consulting',
        label: 'AI consulting',
        title: 'AI Consulting in Minneapolis That Ships Software | minneAI',
        description:
            'Hands-on AI consulting in Minneapolis, led by a former Amazon and Chewy engineer. We find where AI saves your team time, then build it in. Free intro call.',
        eyebrow: 'AI consulting',
        h1: 'AI consulting in Minneapolis.',
        lede:
            'We help businesses figure out where AI actually pays off, then build it into the software they run every day. Working software, not a slide deck.',
        sections: [
            {
                heading: 'Start with the problem, not the technology',
                body: [
                    'Most of our engagements start with a question, not a spec. You have a process, some data, and a pile of manual work, and you want to know if AI is the answer. Sometimes it is. Sometimes a well-built screen and a connection to your ERP does more. We will tell you which.',
                    'When AI is the right tool, we build it in. That can mean computer vision that confirms a step on the production floor, reports written from your own data, or decision support that shows its work. It lives inside the software your team works in every day, not in a separate chat window.',
                ],
            },
            {
                heading: 'Who you work with',
                body: [
                    'minneAI is an AI consulting and software engineering firm in Minneapolis, Minnesota. We work with small businesses and larger operations, remotely or on site around the Twin Cities.',
                    'Paul Tran, founder and principal engineer. He spent more than 15 years building software and leading engineering teams, including at Amazon and Chewy, and teaches AI for Engineering Managers at St. Cloud State University. You work directly with Paul throughout. When a build needs more hands, he brings in senior engineers he trusts, and your point of contact never changes.',
                ],
            },
        ],
        list: {
            heading: 'Where we help',
            items: [
                'Finding the processes where AI saves real hours',
                'Computer vision for inspection and step recognition',
                'Reports and summaries written from your own data',
                'Decision support with every calculation visible',
                'AI features connected to your ERP and back-office systems',
                'AI training and workshops for teams and leaders',
            ],
        },
        related: ['standardiq', 'moneybuddy'],
        faqs: [
            {
                q: 'Do we need to know what we want before we call?',
                a: 'No. Plenty of first calls start with "we think AI could help, but we are not sure where." The free intro call is for working that out.',
            },
            {
                q: 'Do you work with small businesses?',
                a: 'Yes. We work with small businesses and larger operations, remotely or on site around the Twin Cities.',
            },
            {
                q: 'Can AI work with the systems we already have?',
                a: 'That is most of the job. We connect AI features to your ERP and back-office systems so they work from live business data, not a spreadsheet someone exported.',
            },
        ],
    },
    {
        id: 'custom-software',
        path: '/services/custom-software',
        label: 'Custom software',
        title: 'Custom Software Development in Minneapolis | minneAI',
        description:
            'Custom software development in Minneapolis: web, desktop and mobile apps built around how your operation runs, connected to your ERP, and supported after launch.',
        eyebrow: 'Custom software',
        h1: 'Custom software development in Minneapolis.',
        lede:
            'Web, desktop and mobile applications built around how your operation actually runs, connected to the systems you already have, and supported after launch.',
        sections: [
            {
                heading: 'Built around the work',
                body: [
                    'Off-the-shelf software asks you to change how you work to fit it. Custom software goes the other way. We start where the work happens, on the floor, in the office or in the field, and build the tool that fits.',
                    'We also build the parts most firms hand off: firmware, device apps and managed device fleets, plus the cloud behind them. One team, from the device to the dashboard.',
                ],
            },
            {
                heading: 'Connected to what you already run',
                body: [
                    'New software is only useful if it talks to the old software. We integrate with ERPs and back-office systems so orders, employees and inventory come from one place, and nobody has to type the same thing twice.',
                ],
            },
        ],
        list: {
            heading: 'What we build',
            items: [
                'Web, desktop and mobile applications',
                'Shop-floor and workstation apps',
                'ERP and back-office integration',
                'Firmware, device apps and managed device fleets',
                'Dashboards and reporting',
                'Support and changes after launch',
            ],
        },
        related: ['walkport', 'standardiq'],
        faqs: [
            {
                q: 'Do you support the software after launch?',
                a: 'Yes. Ongoing support and changes are usually a monthly arrangement.',
            },
            {
                q: 'Can you take over a project that is already started?',
                a: 'Yes. Picking up something already in progress is a common way an engagement starts.',
            },
            {
                q: 'Can you work with our ERP?',
                a: 'Yes. StandardIQ and ScheduleIQ both pull live data from the ERP, and that kind of integration is a regular part of our work.',
            },
        ],
    },
    {
        id: 'ai-automation',
        path: '/services/ai-automation',
        label: 'AI automation for small businesses',
        title: 'AI Automation for Small Businesses in Minneapolis | minneAI',
        description:
            'AI automation for Minneapolis small businesses. We take data entry, weekly reports and scheduling off your team, connected to the systems you use. Free intro call.',
        eyebrow: 'AI automation',
        h1: 'AI automation for small businesses in Minneapolis.',
        lede:
            'If someone on your team spends hours copying data between systems, building the same report every week, or answering the same questions, software and AI can take that off their plate.',
        sections: [
            {
                heading: 'Start with where the hours go',
                body: [
                    'minneAI is a software engineering and AI consulting firm in Minneapolis, Minnesota. We automate the repetitive work that eats a small team\'s week, and we build it around the tools your team already has.',
                    'Good automation starts with the work, not a tool. On the intro call we look at what repeats, then pick the piece where automation saves the most time for the least risk.',
                    'Then we build it to last: connected to the systems you already use, with people able to see and check what it did. No black boxes.',
                ],
            },
            {
                heading: 'Why a small firm',
                body: [
                    'You work directly with the engineer building it. We bill by the day or hour, or monthly for ongoing work, so you can start small and stop when it is done. AI makes our own work faster too, and we pass those savings on.',
                ],
            },
        ],
        list: {
            heading: 'What we automate',
            items: [
                'Moving data between your ERP, spreadsheets and other systems',
                'Reports and summaries written automatically from your data',
                'Scheduling and planning that pulls live numbers',
                'Process automation that removes manual handoffs between systems',
                'Checks that catch mistakes before they go out the door',
            ],
        },
        related: ['scheduleiq', 'moneybuddy'],
        faqs: [
            {
                q: 'What does it cost?',
                a: 'It depends on the work. We bill by the day or hour, or monthly for ongoing work, rather than fixed bids. Most automation work starts small, one process at a time, so you can see the payoff before taking on more.',
            },
            {
                q: 'Is our business too small for this?',
                a: 'Probably not. We take on a wide range of projects, from a single automation to a full product. If it would save your team real time, it is worth a call.',
            },
        ],
    },
    {
        id: 'working-with-us',
        path: '/working-with-us',
        label: 'Working with us',
        title: 'How Engagements Work: Billing, Process & FAQ | minneAI',
        description:
            'How working with minneAI works: a free intro call, then work billed by the day, hour or month. No fixed bids. Remote or on site in the Twin Cities.',
        eyebrow: 'Working with us',
        h1: 'How working with us works.',
        lede:
            'No fixed bids and no long sales process. A free call to understand the problem, then work billed by the day, hour or month.',
        steps: [
            {
                heading: 'Reach out',
                body: 'Use the contact form or the chat. Paul usually replies within a day.',
            },
            {
                heading: 'A free intro call',
                body: 'We talk through the problem and whether we are the right fit. No cost and no commitment.',
            },
            {
                heading: 'Work, billed simply',
                body: 'By the day or hour, or monthly for ongoing work. You pay for the work done and can change direction as you learn.',
            },
            {
                heading: 'Remote or on site',
                body: 'We work remotely with teams anywhere, and on site around the Twin Cities.',
            },
        ],
        sections: [
            {
                heading: 'Who we work with',
                body: [
                    'Clients range from small businesses to larger operations. The work has run from shop-floor software in manufacturing to the firmware and app for an FDA-registered medical device, and much of it connects to the ERP and back-office systems the client already runs.',
                    'Whatever the size, you work directly with the engineer building it, and the first conversation is free.',
                ],
            },
        ],
        list: {
            heading: 'Common starting points',
            items: [
                'Just exploring and not sure what you need yet',
                'Wanting to automate something in your business',
                'Training or a workshop for your team',
                'Something already started that needs help',
            ],
        },
        related: [],
        faqs: [
            {
                q: 'How much does a project cost?',
                a: 'It depends on the work, so we do not publish rates. Work is billed by the day or hour, or as a monthly arrangement for ongoing work. We scope it together on the free intro call.',
            },
            {
                q: 'Do you do fixed-bid projects?',
                a: 'No. Billing for the time worked keeps the scope honest, lets you change direction without renegotiating, and means you never pay for padding.',
            },
            {
                q: 'What kinds of projects do you take on?',
                a: 'Custom software, AI built into products, firmware and devices, and integration with ERPs and back-office systems. Clients range from small businesses to larger operations.',
            },
            {
                q: 'Where are you located?',
                a: 'Minneapolis, Minnesota. We work remotely with teams anywhere and on site around the Twin Cities.',
            },
            {
                q: 'Who does the work?',
                a: 'Paul Tran, founder and principal engineer, and you work with him directly throughout. When a project needs more hands, he brings in a small bench of senior and principal engineers, and he stays your point of contact.',
            },
            {
                q: 'Do you offer AI training?',
                a: 'Yes. We run AI training and workshops for teams and leaders.',
            },
            {
                q: 'How quickly will I hear back?',
                a: 'Usually within a day, through the contact form or the chat.',
            },
        ],
    },
];

export const findPage = (id) => pages.find((p) => p.id === id);
export const servicePages = pages.filter((p) => p.path.startsWith('/services/'));

export default pages;
